/**
 * Store card is used to summarize the information relating to a store.
 *
 * @module views/components/StoreListItem
 * @memberof -Common
 */
import './StoreListItem.scss';

import React, { useEffect } from 'react';

import classNames from 'classnames';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import List from '@ulta/core/components/List/List';
import RadioButton from '@ulta/core/components/RadioButton_Huge/RadioButton_Huge';
import Spacer from '@ulta/core/components/Spacer/Spacer';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Divider from '@ulta/components/Divider/Divider';

import addressStringFormatter from '@ulta/utils/addressStringFormatter/addressStringFormatter';

import Accordion from '../Accordion/Accordion';
import MiniProductCard from '../MiniProductCard/MiniProductCard';
import Tag from '../Tag/Tag';
import TitleCompact from '../TitleCompact/TitleCompact';
import * as utils from './StoreListItem';

/**
 * Represents a StoreListItem component
 *
 * @method
 * @param {StoreListItemProps} props - React properties passed from composition
 * @returns StoreListItem
 */
export const StoreListItem = function( props ){
  const {
    address1,
    address2,
    availableItemsCount,
    availablePickupTitle,
    availableSkuForPickup,
    componentKey,
    curbsideLabel,
    distance,
    hideInput,
    hideRadioButton = defaultProps.hideRadioButton,
    invokeMutation,
    isEnabled,
    isFullyAvailable,
    isOpenLabel = '',
    isSelected,
    name,
    nonAvailableSkuForPickup,
    notAvailableItemsCount,
    notAvailablPickupTitle,
    notAvailablePickupMessage,
    onStoreSelect,
    pickupitemAvailabilityStatus = {},
    selectAction,
    storeHoursLabel,
    storeId,
    storeName,
    value,
    emphasize,
    isPlp,
    titleHtmlTag = 'h5'
  } = props;

  useEffect( () => {
    if( isSelected && isEnabled ){
      onStoreSelect && onStoreSelect( { storeId, name } );
    }
  }, [] );

  const messageType = pickupitemAvailabilityStatus.color?.includes( '-' ) ?
    pickupitemAvailabilityStatus.color.split( '-' )[0] :
    pickupitemAvailabilityStatus.color;

  const hasAvailabilityStatus = pickupitemAvailabilityStatus.label && ( !isEnabled || isFullyAvailable );
  const hasAvailabilityMessage = !isEnabled || isFullyAvailable || pickupitemAvailabilityStatus;
  const openLabel = isOpenLabel?.includes( '*' ) ? addressStringFormatter( isOpenLabel, '*', 4 ) : isOpenLabel;
  const openStatusLabel = isOpenLabel?.includes( '*' ) && addressStringFormatter( isOpenLabel, '*', 2 );
  const hasStatusText = openStatusLabel || openLabel;
  const pickupitemAvailability = pickupitemAvailabilityStatus?.label && !isPlp;
  const storeItemContent = (
    <div
      className={ classNames( 'StoreListItem__content', {
        [`StoreListItem--disabled`]: !isEnabled
      } ) }
    >
      <div className='StoreListItem__title'>
        <div className='StoreListItem__titleText'>
          <Text htmlTag={ titleHtmlTag }
            textStyle='body-1'
          >
            { name }
          </Text>
        </div>
        <div className='StoreListItem__titleDistance'>
          <Text htmlTag='span'
            textStyle='body-1'
            color='neutral-600'
          >
            { distance }
          </Text>
        </div>

      </div>
      <Spacer value={ '00' } />
      { storeName &&
      <Text htmlTag='p'
        textStyle='body-2'
        color='neutral-600'
      >
        { storeName }
      </Text> }
      <Text htmlTag='p'
        textStyle='body-2'
        color='neutral-600'
      >
        { address1 }
      </Text>
      <Text htmlTag='p'
        textStyle='body-2'
        color='neutral-600'
      >
        { address2 }
      </Text>
      { storeHoursLabel &&
      <>
        <Spacer value={ '02' } />
        <Text htmlTag='p'
          textStyle='eyebrow'
        >
          { storeHoursLabel }
        </Text>
      </>
      }
      { hasStatusText && (
        <div className={
          classNames( 'StoreListItem__statusText',
            {
              'StoreListItem__statusText_bold': emphasize
            } ) }
        >
          { openStatusLabel && (
            <Text textStyle='body-3'
              htmlTag='span'
              color='neutral-900'
              fontWeight='bold'
            >
              { openStatusLabel }
            </Text>
          ) }
          <Text textStyle='body-3'
            htmlTag='span'
            color='neutral-600'
          >
            { openLabel }
          </Text>
        </div>
      ) }
      <Text htmlTag='p'
        textStyle='body-3'
        color='neutral-600'
      >
        { curbsideLabel }
      </Text>

    </div>
  );

  return (
    <div className='StoreListItem'>
      <div className='StoreListItem__radio'>
        { !hideRadioButton && (
          <Formik initialValues={ {} }>
            <RadioButton
              name='primary'
              id={ storeId }
              isChecked={ isSelected }
              value={ value }
              hideInput={ hideInput }
              isDisabled={ !isEnabled }
              onChange={ () =>
                utils.handleStoreSelect( { selectAction, storeProps: { storeId, name } }, { onStoreSelect, invokeMutation } )
              }
              unstyled
              className='StoreListItem__label'
              ariaDescribedBy={ `${storeId}_Description` }
            >
              { storeItemContent }
            </RadioButton>
          </Formik>
        ) }
        { hideRadioButton && storeItemContent }
      </div>
      { pickupitemAvailability &&
      <div
        className={ classNames( {
          [`StoreListItem__availabilityMessage`]: hasAvailabilityMessage
        } ) }
      >
        { pickupitemAvailabilityStatus?.label && isEnabled && !isFullyAvailable && (
          <Accordion
            label={ pickupitemAvailabilityStatus?.label }
            showTag={ true }
            showBorder={ false }
            messageType={ messageType }
            id={ `${storeId}_Description` }
            open={ !isFullyAvailable && isSelected }
          >
            { hasItems( availableSkuForPickup ) && (
              <>
                <TitleCompact
                  htmlTag='h3'
                  title={ availablePickupTitle }
                  subTitle={ availableItemsCount }
                  showDivider={ true }
                />
                <Spacer value={ '03' } />
                <List display='Block'
                  spacer='03'
                  separator='Muted'
                >
                  { availableSkuForPickup.map( ( item, index ) => (
                    <MiniProductCard key={ `${componentKey}-is-available-${index}` }
                      { ...spreadProps( { ...item } ) }
                    />
                  ) ) }
                </List>
              </>
            ) }

            { hasItems( nonAvailableSkuForPickup ) && (
              <>
                <div className='StoreListItem__notAvailabilityMessage'>
                  <TitleCompact
                    htmlTag='h3'
                    title={ notAvailablPickupTitle }
                    subTitle={ notAvailableItemsCount }
                  />
                  { ( notAvailablePickupMessage &&
                  <InlineMessage
                    { ...spreadProps( { ...notAvailablePickupMessage } ) }
                    messageType={ notAvailablePickupMessage?.icon }
                    role={ 'status' }
                  />
                  ) }
                  <Divider
                    type={ 'gray' }
                  />
                </div>
                <Spacer value={ '03' }/>
                <List display='Block'
                  spacer='02'
                  separator='Muted'
                >
                  { nonAvailableSkuForPickup.map( ( item, index ) => {
                    return (
                      <MiniProductCard
                        { ...spreadProps( { ...item } ) }
                        key={ `${componentKey}-not-available-${index}` }
                      />
                    );
                  } ) }
                </List>
              </>
            ) }
          </Accordion>
        ) }
        { hasAvailabilityStatus &&
          <div data-testid='StoreListItem'
            id={ `${storeId}_Description` }
          >
            <Tag messageText={ pickupitemAvailabilityStatus.label }
              messageType={ messageType }
            />
          </div>
        }
      </div> }
    </div>
  );
};

/**
 * handleStoreSelect function for store selection mutation
* @method
* @param {Object} data - Passing required data as an argument
* @param {Object} methods - Passing invokeMutation method as an argument
*/
export const handleStoreSelect = ( data, methods ) => {
  const { storeProps, selectAction, storeId } = data || {};
  const { onStoreSelect, invokeMutation } = methods || {};
  const { graphql, customHeaders, params } = selectAction || {};

  onStoreSelect && onStoreSelect( storeProps );

  if( !invokeMutation || !graphql ){
    return;
  }

  invokeMutation( {
    graphql,
    customHeaders,
    params,
    variables: {
      moduleParams: {
        storeId
      },
      url: { path: !isServer() && global.location.pathname }
    }
  } );
};

/**
 * Property type definitions
 * @typedef StoreListItemProps
 * @type {object}
 * @property {boolean} isChecked - Set the radio button is checked or unchecked
 * @property {boolean} hideInput - for hiding the radio button
 * @property {boolean} isDisabled - the radio button is un-clickable
 * @property {string} id - Sets id for radio button
 * @property {string} name - sets the store name
 * @property {string} address1 - sets the store address1
 * @property {string} address2 - sets the store address2
 * @property {string} distance - sets the distance
 * @property {string} storeHoursLabel - the title for showing the store hours
 * @property {string} isOpenLabel - sets the store hours
 * @property {boolean} isOpen - will be true if store is open
 * @property {string} curbsideLabel - sets the curbsideLabel of the store
 * @property {boolean} isSelected - sets the isSelected
 * @property {string} availableItemsCount - sets the availableItemsCount of the store
 * @property {object} notAvailablePickupMessage - sets the notAvailablePickupMessage of the store
 * @property {string} notAvailableItemsCount - sets the notAvailableItemsCount of the store
 * @property {array} nonAvailableSkuForPickup - not available items list
 * @property {boolean} isEnabled - available items list
 * @property {boolean} isFullyAvailable - sets the curbsideLabel of the store
 * @property {object} pickupitemAvailabilityStatus - sets the curbsideLabel of the store
 * @property {array} availableSkuForPickup - available items list
 * @property {string} storeId - unique store id
 * @property {string} storeName - unique store name
 * @property {object} selectAction - Radiobutton selection action
 * @property {func} onStoreSelect - Triggered when a store is selected on radioButton change
 * @property {func} invokeMutation - Called when a store is selected on radioButton change
 * @property {boolean} hideRadioButton - boolean to display/hide radio selection
 * @property {string} titleHtmlTag - html tag for title
 */
export const propTypes = {
  isChecked: PropTypes.bool,
  hideInput: PropTypes.bool,
  isDisabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  distance: PropTypes.string,
  storeHoursLabel: PropTypes.string,
  isOpenLabel: PropTypes.string,
  isOpen: PropTypes.bool,
  curbsideLabel: PropTypes.string,
  isSelected: PropTypes.bool,
  availableItemsCount: PropTypes.string,
  notAvailablePickupMessage: PropTypes.shape( {
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    icon: PropTypes.string,
    message: PropTypes.string,
    textColor: PropTypes.string,
    action1: PropTypes.object,
    action2: PropTypes.object,
    dismissAction: PropTypes.object
  } ),
  notAvailableItemsCount: PropTypes.string,
  isEnabled: PropTypes.bool,
  isFullyAvailable: PropTypes.bool,
  pickupitemAvailabilityStatus: PropTypes.shape( {
    label: PropTypes.string.isRequired,
    color: PropTypes.string
  } ),
  availableSkuForPickup: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantLabel: PropTypes.string,
      quantity: PropTypes.string,
      image: PropTypes.object,
      variantImage: PropTypes.object,
      _skuId: PropTypes.string
    } )
  ),
  nonAvailableSkuForPickup: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantLabel: PropTypes.string,
      quantity: PropTypes.string,
      image: PropTypes.object,
      variantImage: PropTypes.object,
      _skuId: PropTypes.string
    } )
  ),
  storeId: PropTypes.string.isRequired,
  storeName: PropTypes.string,
  selectAction: PropTypes.shape( {
    graphql: PropTypes.string.isRequired
  } ),
  onStoreSelect: PropTypes.func,
  invokeMutation: PropTypes.func,
  hideRadioButton: PropTypes.bool,
  titleHtmlTag: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} hideRadioButton=false - Radio button is hidden byt default
 */
export const defaultProps =  {
  hideRadioButton: false
};

StoreListItem.propTypes = propTypes;
StoreListItem.defaultProps = defaultProps;

export default StoreListItem;
